import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Lightbulb, PlayCircle, Search } from "lucide-react";
import { listGuides, type Guide, type GuideModule } from "@/api/guides";
import { Main } from "@/components/layout/main";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { GuideVideoModal } from "@/components/guide-assistant/guide-video-modal";
import { getYoutubeThumbnailUrl } from "@/lib/youtube";
import { cn } from "@/lib/utils";
import { GUIDE_MODULES, getModuleIcon } from "./guide-modules";

export function Guides() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [module, setModule] = useState<GuideModule | "all">("all");
  const [selected, setSelected] = useState<Guide | null>(null);

  const { data: guides = [], isLoading } = useQuery({
    queryKey: ["guides", "all"],
    queryFn: () => listGuides({ ordering: "created_at" }),
    staleTime: 10 * 60 * 1000,
  });

  const counts = useMemo(() => {
    const map: Partial<Record<GuideModule, number>> = {};
    guides.forEach((g) => {
      map[g.functionality] = (map[g.functionality] ?? 0) + 1;
    });
    return map;
  }, [guides]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guides.filter((g) => {
      if (module !== "all" && g.functionality !== module) return false;
      if (!q) return true;
      return (
        g.title.toLowerCase().includes(q) ||
        g.body.toLowerCase().includes(q) ||
        g.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    });
  }, [guides, module, search]);

  const tips = useMemo(() => {
    if (module === "all") return [];
    const res = t(`guides.tips.${module}`, { returnObjects: true, defaultValue: [] });
    return Array.isArray(res) ? (res as string[]) : [];
  }, [module, t]);

  const visibleModules = GUIDE_MODULES.filter((m) => counts[m.key]);

  return (
    <Main>
      <PageHeader
        title={t("guides.page_title", { defaultValue: "Guides et ressources" })}
        description={t("guides.page_desc", {
          defaultValue: "Vidéos explicatives et bonnes pratiques pour tirer le meilleur d'AvocatPro",
        })}
      />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("guides.search_placeholder", { defaultValue: "Rechercher un guide..." })}
          className="ps-9"
        />
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={module === "all" ? "default" : "outline"}
          onClick={() => setModule("all")}
        >
          {t("guides.all", { defaultValue: "Tous" })}
          <Badge variant="secondary" className="ms-2">{guides.length}</Badge>
        </Button>
        {visibleModules.map(({ key, icon: Icon }) => (
          <Button
            key={key}
            size="sm"
            variant={module === key ? "default" : "outline"}
            onClick={() => setModule(key)}
          >
            <Icon className="me-1.5 h-4 w-4" />
            {t(`guides.modules.${key}`, { defaultValue: key })}
            <Badge variant="secondary" className="ms-2">{counts[key]}</Badge>
          </Button>
        ))}
      </div>

      {tips.length > 0 && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Lightbulb className="h-4 w-4 text-primary" />
              {t("guides.best_practices", { defaultValue: "Bonnes pratiques" })} —{" "}
              {t(`guides.modules.${module}`, { defaultValue: module })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1.5 ps-5 text-sm">
              {tips.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground">
          {t("guides.empty", { defaultValue: "Aucun guide trouvé." })}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((guide) => {
            const Icon = getModuleIcon(guide.functionality);
            const thumb = getYoutubeThumbnailUrl(guide.link);
            return (
              <Card
                key={guide.id}
                className="group cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
                onClick={() => setSelected(guide)}
              >
                <div className="relative aspect-video bg-muted">
                  {thumb && (
                    <img
                      src={thumb}
                      alt={guide.title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/40">
                    <PlayCircle className="h-12 w-12 text-white drop-shadow" />
                  </div>
                </div>
                <CardContent className="space-y-2 p-4">
                  <Badge variant="outline" className={cn("gap-1")}>
                    <Icon className="h-3 w-3" />
                    {t(`guides.modules.${guide.functionality}`, { defaultValue: guide.functionality })}
                  </Badge>
                  <h3 className="line-clamp-2 text-sm font-semibold leading-snug">{guide.title}</h3>
                  {guide.body && (
                    <p className="line-clamp-3 text-xs text-muted-foreground">{guide.body}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <GuideVideoModal
        guide={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </Main>
  );
}
